import React from 'react'
import { ContentStateInterface, FlowMetaData, OnFieldChangeType, Screen, ScreenProperty } from './types'
import { AbstractScreenValidationResult, ValidationSchema } from './validation-types'
import { PriceToApiMapper } from '../api/realEstate/converters/toApi/estate/price/priceToApi'
import { CourtageInformationToApiMapper } from '../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import { ApiSearchDataToApiMapper } from '../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'
import { FeatureSwitch } from '../utils/features/featureSwitches'

export type FlowInformation = {
  owner: string
  offerType?: string
  estateType: string
  name: FlowName
  path: string
  validationSchema: ValidationSchema
  metaData: FlowMetaData
  options?: FlowOptions
  priceToApiMapper?: PriceToApiMapper
  courtageToApiMapper?: CourtageInformationToApiMapper
  apiSearchDataToApiMapper?: ApiSearchDataToApiMapper
  isMobileFlow?: boolean
}

export type FlowOptions = {
  hasTextSuggestions?: boolean // generated by Retresco for description fields
  textSuggestionsFeature?: FeatureSwitch
  depositCalculationEnabled?: boolean
  recommendedPriceEnabled?: boolean
  isWaitingListFlow?: boolean
  hasHeyImmo?: boolean
  hasAiDataExtractionBanner?: boolean
  customPageHeader?: {
    titleKey?: string
    descriptionKey?: string
  }
}

export enum FlowName {
  //property_owner_rent
  PROPERTY_OWNER_APARTMENT_RENT = 'property_owner_apartment_rent',
  PROPERTY_OWNER_GARAGE_RENT = 'property_owner_garage_rent',
  PROPERTY_OWNER_GASTRONOMY_RENT = 'property_owner_gastronomy_rent',
  PROPERTY_OWNER_HANGAR_RENT = 'property_owner_hangar_rent',
  PROPERTY_OWNER_HOUSE_RENT = 'property_owner_house_rent',
  PROPERTY_OWNER_OFFICE_RENT = 'property_owner_office_rent',
  PROPERTY_OWNER_STORE_RENT = 'property_owner_store_rent',
  PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT = 'property_owner_short_term_accommodation_rent',
  PROPERTY_OWNER_INDUSTRIAL_PLOT_RENT = 'property_owner_industrial_plot_rent',

  //property_owner_sell
  PROPERTY_OWNER_APARTMENT_SELL = 'property_owner_apartment_sell',
  PROPERTY_OWNER_GASTRONOMY_SELL = 'property_owner_gastronomy_sell',
  PROPERTY_OWNER_HANGAR_SELL = 'property_owner_hangar_sell',
  PROPERTY_OWNER_HOUSE_SELL = 'property_owner_house_sell',
  PROPERTY_OWNER_INDUSTRIAL_PLOT_SELL = 'property_owner_industrial_plot_sell',
  PROPERTY_OWNER_OFFICE_SELL = 'property_owner_office_sell',
  PROPERTY_OWNER_PLOT_SELL = 'property_owner_plot_sell',
  PROPERTY_OWNER_STORE_SELL = 'property_owner_store_sell',
  PROPERTY_OWNER_INVESTMENT_SELL = 'property_owner_investment_sell',
  PROPERTY_OWNER_GARAGE_SELL = 'property_owner_garage_sell',

  //tenant_rent
  TENANT_APARTMENT_RENT = 'tenant_apartment_rent',
  TENANT_GARAGE_RENT = 'tenant_garage_rent',
  TENANT_GASTRONOMY_RENT = 'tenant_gastronomy_rent',
  TENANT_HANGAR_RENT = 'tenant_hangar_rent',
  TENANT_HOUSE_RENT = 'tenant_house_rent',
  TENANT_OFFICE_RENT = 'tenant_office_rent',
  TENANT_SHORT_TERM_ACCOMMODATION_RENT = 'tenant_short_term_accommodation_rent',
  TENANT_STORE_RENT = 'tenant_store_rent',
  TENANT_FLAT_SHARE_ROOM_RENT = 'tenant_flat_share_room_rent',
  TENANT_INDUSTRIAL_PLOT_RENT = 'tenant_industrial_plot_rent',

  //agent_rent
  AGENT_APARTMENT_RENT = 'agent_apartment_rent',
  AGENT_FLAT_SHARE_ROOM_RENT = 'agent_flat_share_room_rent',
  AGENT_SHORT_TERM_ACCOMMODATION_RENT = 'agent_short_term_accommodation_rent',
  AGENT_GARAGE_RENT = 'agent_garage_rent',
  AGENT_HOUSE_RENT = 'agent_house_rent',
  AGENT_OFFICE_RENT = 'agent_office_rent',
  AGENT_GASTRONOMY_RENT = 'agent_gastronomy_rent',
  AGENT_STORE_RENT = 'agent_store_rent',
  AGENT_PRODUCTION_HALL_RENT = 'agent_production_hall_rent',
  AGENT_INDUSTRIAL_PLOT_RENT = 'agent_industrial_plot_rent',
  AGENT_WAITING_LIST_APARTMENT_RENT = 'agent_waiting_list_apartment_rent',

  //agent_sell
  AGENT_APARTMENT_SELL = 'agent_apartment_sell',
  AGENT_HOUSE_SELL = 'agent_house_sell',
  AGENT_GASTRONOMY_SELL = 'agent_gastronomy_sell',
  AGENT_OFFICE_SELL = 'agent_office_sell',
  AGENT_PRODUCTION_HALL_SELL = 'agent_production_hall_sell',
  AGENT_INDUSTRIAL_PLOT_SELL = 'agent_industrial_plot_sell',
  AGENT_STORE_SELL = 'agent_store_sell',
  AGENT_PLOT_SELL = 'agent_plot_sell',
  AGENT_INVESTMENT_SELL = 'agent_investment_sell',
  AGENT_GARAGE_SELL = 'agent_garage_sell',
}

export interface FlowScreenElement {
  screen: Screen
  element: (props: FlowElementProps) => React.ReactElement
  tooltip?: unknown
  feature?: FeatureSwitch
}

export interface FlowScreen {
  screen: Screen
}

export interface Flow {
  flowInformation: FlowInformation
  defaultStateValues: ContentStateInterface
  flowScreens: FlowScreenElement[]
}

export interface FlowElementProps {
  flowInformation?: FlowInformation
  insertionData: ContentStateInterface
  flowName: FlowName
  validationMessages?: AbstractScreenValidationResult
  onNavigate: (screen: Screen) => void
  onChange: OnFieldChangeType
  doReset: (clearingUpOf: ScreenProperty[]) => void
  doCloseMultimediaStatusMessage?: () => { type: string }
}
